'use client';

import { useState } from 'react';
import Link from 'next/link';
import { ArrowLeft, CheckCircle2 } from 'lucide-react';
import { esqueciSenha } from '@/services/auth';
import { useSystemName } from '@/hooks/useSystemName';
import SystemLogo from '@/components/SystemLogo';

export default function EsqueciSenhaPage() {
  const [identificador, setIdentificador] = useState('');
  const [mensagem, setMensagem] = useState('');
  const [enviado, setEnviado] = useState(false);
  const [loading, setLoading] = useState(false);
  const nomeSistema = useSystemName();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!identificador.trim()) return;

    setLoading(true);

    try {
      const response = await esqueciSenha({
        identificador,
      });
      setMensagem(
        response?.message ||
          'Se o identificador informado existir, você receberá as instruções para redefinir sua senha.',
      );
      setEnviado(true);
    } catch {
      // Backend sempre responde 200; em caso de falha de rede mostramos a mesma mensagem genérica.
      setMensagem(
        'Se o identificador informado existir, você receberá as instruções para redefinir sua senha.',
      );
      setEnviado(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <header className="bg-white shadow-md border-b border-gray-200">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            {/* Nome do Sistema - Esquerda */}
            <div className="flex items-center min-w-0">
              <Link href="/" className="text-lg sm:text-xl font-bold text-gray-800 hover:text-blue-600 transition-colors truncate">
                {nomeSistema}
              </Link>
            </div>

            {/* Logo - Centro */}
            <div className="flex-1 flex justify-center">
              <Link href="/" className="flex items-center">
                <SystemLogo className="h-9 sm:h-12 object-contain" fallbackToName />
              </Link>
            </div>

            {/* Espaço vazio para manter alinhamento - Direita */}
            <div className="hidden sm:block w-32"></div>
          </div>
        </div>
      </header>

      {/* Conteúdo */}
      <div className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="bg-white rounded-lg shadow-xl p-6 sm:p-8 w-full max-w-md">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-800 mb-2">Esqueci minha senha</h1>
            <p className="text-gray-600">
              Informe seu e-mail ou usuário para receber as instruções de redefinição.
            </p>
          </div>

          {enviado ? (
            <div className="space-y-6">
              <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 mt-0.5 shrink-0" aria-hidden />
                <span>{mensagem}</span>
              </div>

              <div className="text-center">
                <Link
                  href="/login"
                  className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-blue-600 transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" aria-hidden />
                  Voltar para o login
                </Link>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="identificador" className="block text-sm font-medium text-gray-700 mb-2">
                  E-mail ou usuário
                </label>
                <input
                  type="text"
                  id="identificador"
                  value={identificador}
                  onChange={(e) => setIdentificador(e.target.value)}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Digite seu e-mail ou usuário"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full btn-primary py-2 px-4 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? 'Enviando...' : 'Enviar instruções'}
              </button>

              <div className="text-center">
                <Link
                  href="/login"
                  className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-blue-600 transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" aria-hidden />
                  Voltar para o login
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
